import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Loader2 } from "lucide-react";

export function AuthDialog() {
  const { authOpen, setAuthOpen, authMode, setAuthMode, login, register } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "buyer" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isLogin = authMode === "login";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
        toast.success(t("auth.loginSuccess"));
      } else {
        await register(form);
        toast.success(t("auth.registerSuccess"));
      }
      setAuthOpen(false);
      setForm({ name: "", email: "", password: "", role: "buyer" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0" data-testid="auth-dialog">
        <DialogTitle className="sr-only">{isLogin ? t("auth.loginTitle") : t("auth.registerTitle")}</DialogTitle>
        <DialogDescription className="sr-only">LuminaStock authentication</DialogDescription>
        <div className="bg-slate-900 px-8 py-8 text-white">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white">
              <Camera size={16} className="text-slate-900" />
            </div>
            <span className="font-display text-lg font-extrabold">LuminaStock</span>
          </div>
          <h2 className="mt-5 font-display text-2xl font-bold">
            {isLogin ? t("auth.loginTitle") : t("auth.registerTitle")}
          </h2>
        </div>

        <form onSubmit={submit} className="space-y-4 px-8 py-7">
          {!isLogin && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("auth.name")}</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-blue-500"
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("auth.email")}</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-blue-500"
              data-testid="auth-email-input"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("auth.password")}</label>
            <input
              type="password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-blue-500"
              data-testid="auth-password-input"
            />
          </div>
          {!isLogin && (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("auth.role")}</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "buyer", label: t("auth.roleBuyer") },
                  { v: "contributor", label: t("auth.roleContributor") },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setForm({ ...form, role: opt.v })}
                    className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition ${
                      form.role === opt.v
                        ? "border-blue-600 bg-blue-50 text-blue-700"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                    data-testid={`auth-role-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 py-3 text-sm font-semibold text-white hover:bg-slate-800 transition disabled:opacity-60"
            data-testid="auth-submit-btn"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {isLogin ? t("auth.loginBtn") : t("auth.registerBtn")}
          </button>

          <p className="text-center text-sm text-slate-500">
            {isLogin ? t("auth.noAccount") : t("auth.hasAccount")}{" "}
            <button
              type="button"
              onClick={() => {
                setError("");
                setAuthMode(isLogin ? "register" : "login");
              }}
              className="font-semibold text-blue-600 hover:underline"
              data-testid="auth-switch-mode"
            >
              {isLogin ? t("auth.switchRegister") : t("auth.switchLogin")}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
